import { useEffect, useRef, useState } from 'react';
import { loadGoogleMaps } from '../../lib/googleMaps';
import { Icons } from './Icons';

export type AddressValue = {
  formatted: string;
  zip: string;
  city: string;
  state: string;
};

// Address input with Google Places autocomplete and a graceful plain-text fallback.
export function AddressField({
  value,
  onChange,
  placeholder = 'Start typing the home address…',
  invalid,
}: {
  value: AddressValue;
  onChange: (v: AddressValue) => void;
  placeholder?: string;
  invalid?: boolean;
}) {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const acRef = useRef<google.maps.places.Autocomplete | null>(null);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    let cancelled = false;
    loadGoogleMaps().then((ok) => {
      if (!ok || cancelled || !inputRef.current || acRef.current) return;
      const ac = new google.maps.places.Autocomplete(inputRef.current, {
        types: ['address'],
        componentRestrictions: { country: ['us', 'ca'] },
        fields: ['address_components', 'formatted_address'],
      });
      ac.addListener('place_changed', () => {
        const place = ac.getPlace();
        const comp = place.address_components ?? [];
        const get = (type: string) => comp.find((c) => c.types.includes(type));
        const zip = get('postal_code')?.long_name ?? '';
        const city = get('locality')?.long_name ?? get('postal_town')?.long_name ?? get('sublocality')?.long_name ?? '';
        const state = get('administrative_area_level_1')?.short_name ?? '';
        const formatted = place.formatted_address ?? inputRef.current!.value;
        // Keep the (uncontrolled) input's visible text in sync with the picked place.
        if (inputRef.current) inputRef.current.value = formatted;
        onChange({ formatted, zip, city, state });
      });
      acRef.current = ac;
      setReady(true);
    });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="relative">
      <span className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-plum/40">
        <Icons.pin width={18} height={18} />
      </span>
      <input
        ref={inputRef}
        defaultValue={value.formatted}
        onChange={(e) => onChange({ ...value, formatted: e.target.value })}
        placeholder={placeholder}
        autoComplete="off"
        className={`w-full rounded-2xl border bg-white py-3.5 pl-11 pr-4 font-body text-plum outline-none transition-colors placeholder:text-plum/40 ${
          invalid ? 'border-terracotta shadow-glow' : 'border-stone focus:border-terracotta focus:shadow-glow'
        }`}
      />
      {ready && value.city && value.state && (
        <p className="mt-1.5 flex items-center gap-1.5 font-body text-xs text-sage-deep">
          <Icons.check width={14} height={14} /> {value.city}, {value.state} {value.zip}
        </p>
      )}
    </div>
  );
}

export const emptyAddress = (): AddressValue => ({ formatted: '', zip: '', city: '', state: '' });
